import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { Token } from '@proto/store/api/auth/resources';
import {
  AuthCredentialRequest,
  SendVerifyCodeRequest,
  SendVerifyCodeResponse,
  SignInWithVerifyCodeRequest,
} from '@proto/store/api/auth/v1/auth';
import { RefreshRequest } from '@proto/store/api/auth/v1/token';
import { AuthInterface } from '@service/auth.interface';
import { LativAPIInterceptor, LativHttpClient } from '@service/lativ.interceptor';
import {
  DeleteAccountRequest,
  GetReceiptSettingsRequest,
  GetReceiptSettingsResponse,
  LinkWithVerifyCodeRequest,
  UpdateProfileRequest,
  UpdateReceiptSettingsRequest,
  UpdateReceiptSettingsResponse,
} from '@proto/store/api/auth/v1/account';
import { PreferenceKey } from '@helper/preference-key';
import { AuthStateService } from '@service/auth-state.service';
import { CryptHelper } from '@helper/crypt-helper';

import { v4 as uuid } from 'uuid';
import decode from 'jwt-decode';
import { catchError, EMPTY, from, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { getAuth, signInWithCustomToken, UserCredential } from 'firebase/auth';
import Bugsnag from '@bugsnag/js';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements AuthInterface {
  private readonly receiptSettings = 'receiptSettings';
  private readonly grpcInstanceId = 'Grpc-Metadata-lativ-store-instance-id';
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly meta = { [this.grpcInstanceId]: '', [LativAPIInterceptor.lativNoToken]: '' };
  private readonly refreshBufferSeconds = 3 * 60;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private authStateService: AuthStateService,
    private lativHttp: LativHttpClient
  ) {}

  newInstanceId(): string {
    if (!this.isBrowser) {
      return '';
    }
    const newId = uuid().replace(/-/g, '');
    try {
      localStorage?.setItem(PreferenceKey.INSTANCE_ID, newId);
    } catch {
      // ignore
    }
    return newId;
  }

  getInstanceId(): string {
    if (!this.isBrowser) {
      return '';
    }
    try {
      return localStorage?.getItem(PreferenceKey.INSTANCE_ID) ?? '';
    } catch {
      return '';
    }
  }

  refreshTokenIfNeeded(): Observable<boolean> {
    if (!this.isBrowser) {
      return EMPTY;
    }
    const authToken = this.authStateService.getToken();
    if (!authToken || !authToken.accessToken) {
      return of(false);
    }
    const decodeToken = decode(authToken.accessToken);
    if (!decodeToken || !decodeToken.exp || !authToken.refreshToken) {
      this.authStateService.logout();
      return of(false);
    }
    const nowInSeconds = Math.floor(Date.now() / 1000);
    if (decodeToken.exp > nowInSeconds + this.refreshBufferSeconds) {
      return of(false);
    }
    let refreshToken = '';
    try {
      refreshToken = atob(CryptHelper.decryptWithKey(authToken.refreshToken, this.getInstanceId()));
    } catch (e) {
      Bugsnag.notify(new Error(`Parse refresh token failed:${e}, uid: ${this.authStateService.getIDToken()?.uid}`));
      this.authStateService.logout();
      return of(false);
    }
    if (!refreshToken) {
      Bugsnag.notify(new Error(`Empty refresh token for uid: ${this.authStateService.getIDToken()?.uid}`));
      this.authStateService.logout();
      return of(false);
    }
    return this.refresh({
      grantType: 'refresh_token',
      refreshToken,
    }).pipe(
      catchError(err => {
        if (err?.status === 502) {
          Bugsnag.notify(
            new Error(
              `502 bad gateway logout user:${err?.error?.message}, uid: ${this.authStateService.getIDToken()?.uid}`
            )
          );
          this.authStateService.logout();
          return of(undefined);
        }
        return throwError(() => err);
      }),
      tap((data: Token | undefined) => {
        const newToken = data?.accessToken;
        if (newToken) {
          authToken.accessToken = newToken;
          this.authStateService.setToken(authToken);
        }
      }),
      map(token => !!token)
    );
  }

  signInWithAuthCredential(req: AuthCredentialRequest): Observable<Token> {
    this.meta[this.grpcInstanceId] = this.getInstanceId() || this.newInstanceId();
    return this.lativHttp
      .post('v1/auth/signInCredential', AuthCredentialRequest.toJSON(req), undefined, this.meta)
      .pipe(
        map(res => this.encryptToken(Token.fromJSON(res))),
        switchMap(token => this.signInFirebase(token.firebaseToken).pipe(map(() => token)))
      );
  }

  refresh(request: RefreshRequest): Observable<Token> {
    this.meta[this.grpcInstanceId] = this.getInstanceId();
    return this.lativHttp
      .post('v1/token/refresh', RefreshRequest.toJSON(request), undefined, this.meta)
      .pipe(map(res => this.encryptToken(Token.fromJSON(res))));
  }

  updateProfile(req: UpdateProfileRequest): Observable<Token> {
    return this.refreshTokenIfNeeded().pipe(
      switchMap(() =>
        this.lativHttp.post('v1/account/profile', UpdateProfileRequest.toJSON(req)).pipe(
          map(res => this.encryptToken(Token.fromJSON(res))),
          tap(res => {
            const token = this.authStateService.getToken();
            token.idToken = res.idToken;
            this.authStateService.setToken(token);
          })
        )
      )
    );
  }

  sendVerifyCode(request: SendVerifyCodeRequest, noToken?: boolean): Observable<SendVerifyCodeResponse> {
    const localMeta = { ...this.meta };
    if (noToken) {
      // 登入後, 有 token 情況, 發送驗證簡訊, 後端會認為是修改手機號碼, 如果登入後要單純驗證使用, 就用 noToken.
      localMeta[LativAPIInterceptor.lativNoToken] = LativAPIInterceptor.lativTrueValue;
    }
    return this.refreshTokenIfNeeded().pipe(
      switchMap(() =>
        this.lativHttp
          .post('v1/auth/sendVerifyCode', SendVerifyCodeRequest.toJSON(request), undefined, localMeta)
          .pipe(map(res => SendVerifyCodeResponse.fromJSON(res)))
      )
    );
  }

  signInWithVerifyCode(request: SignInWithVerifyCodeRequest): Observable<Token> {
    this.meta[this.grpcInstanceId] = this.getInstanceId() || this.newInstanceId();
    return this.lativHttp
      .post('v1/auth/signInVerifyCode', SignInWithVerifyCodeRequest.toJSON(request), undefined, this.meta)
      .pipe(
        map(res => this.encryptToken(Token.fromJSON(res))),
        switchMap(token => this.signInFirebase(token.firebaseToken).pipe(map(() => token)))
      );
  }

  linkWithVerifyCode(request: LinkWithVerifyCodeRequest): Observable<Token> {
    this.meta[this.grpcInstanceId] = this.getInstanceId();
    return this.refreshTokenIfNeeded().pipe(
      switchMap(() =>
        this.lativHttp
          .post('v1/account/profile:linkVerifyCode', LinkWithVerifyCodeRequest.toJSON(request), undefined, this.meta)
          .pipe(map(res => this.encryptToken(Token.fromJSON(res))))
      )
    );
  }

  getReceiptSettings(req: GetReceiptSettingsRequest): Observable<GetReceiptSettingsResponse> {
    return this.refreshTokenIfNeeded().pipe(
      switchMap(() =>
        this.lativHttp
          .get(`v1/account/profile:${this.receiptSettings}`, {
            params: GetReceiptSettingsRequest.toJSON(req) as {
              [param: string]: string;
            },
          })
          .pipe(map(res => GetReceiptSettingsResponse.fromJSON(res)))
      )
    );
  }

  updateReceiptSettings(request: UpdateReceiptSettingsRequest): Observable<UpdateReceiptSettingsResponse> {
    return this.refreshTokenIfNeeded().pipe(
      switchMap(() =>
        this.lativHttp
          .post(
            `v1/account/profile:${this.receiptSettings}`,
            UpdateReceiptSettingsRequest.toJSON(request),
            undefined,
            this.meta
          )
          .pipe(map(res => UpdateReceiptSettingsResponse.fromJSON(res)))
      )
    );
  }

  deleteAccount(req: DeleteAccountRequest): Observable<unknown> {
    return this.refreshTokenIfNeeded().pipe(
      switchMap(() => this.lativHttp.post('v1/account', DeleteAccountRequest.toJSON(req)))
    );
  }

  private encryptToken(token: Token): Token {
    const id = this.getInstanceId();
    if (token.accessToken) {
      token.accessToken = CryptHelper.encryptWithKey(btoa(token.accessToken), id);
    }
    if (token.refreshToken) {
      token.refreshToken = CryptHelper.encryptWithKey(btoa(token.refreshToken), id);
    }
    return token;
  }

  private signInFirebase(token: string): Observable<UserCredential> {
    return from(signInWithCustomToken(getAuth(), token));
  }
}
